import { useState, useRef, useEffect, useCallback } from 'react';
import { FlatList } from 'react-native';

import { scale } from '@components/frame';
import { isWeb, localTime } from '@helpers';
import { getBarLegend } from '@components/machines/components/eventsChart/calculation';
import { getPeriodStep } from '@components/machines/components/eventsChart/helpers/period';
import { useHandleParams } from '@v2/pages/machines/hooks/useHandleParams';
import { useFocusEffect } from '@react-navigation/native';
import { TimePeriod } from '@v2/pages/machines/common/enums';
import { UseManageUtilizationChartParams, UseManageUtilizationChartReturn } from '@components/machines/components/Utilization/components/chart/types';

export const useManageUtilizationChart = ({
  selectedDate,
  chartWidth,
  id,
  setSelectedChartDate,
  resetMobileChartCount,
  setTimePeriod,
}: UseManageUtilizationChartParams): UseManageUtilizationChartReturn => {
  const { timePeriod: selectedTime } = useHandleParams();

  const [selectedBarIndex, setSelectedBarIndex] = useState(null);

  useFocusEffect(
    useCallback(() => {
      setSelectedBarIndex(null);
    }, []),
  );

  useEffect(() => {
    setSelectedBarIndex(null);
  }, [selectedDate, selectedTime, id]);

  const toGo = (selected, configOnly = false) => {
    const nextTime = {
      [TimePeriod.YEAR]: TimePeriod.MONTH,
      [TimePeriod.MONTH]: TimePeriod.DAY,
      [TimePeriod.WEEK]: TimePeriod.DAY,
    };

    if (configOnly) {
      return {
        section: nextTime[selectedTime],
        date: selected.dateISO,
      };
    }

    if (selectedTime === TimePeriod.DAY) {
      return;
    }

    setSelectedChartDate(selected.dateISO);
    setTimePeriod(nextTime[selectedTime]);
  };

  const { legend = [] } = getBarLegend(selectedTime, selectedDate);
  const barAmount = legend.length;

  const flatListRef = useRef();

  const getDate = ({ initial, action, amount, start, length = barAmount }) => {
    const { unit, count } = getPeriodStep(selectedTime);
    let date = localTime(initial);
    if (action) {
      switch (action) {
        case 'subtract': {
          date.subtract(amount * count, unit);
          break;
        }
        case 'add': {
          date.add(amount * count, unit);
          break;
        }
      }
    }

    date = date.format('YYYY-MM-DD');

    if (localTime(date).endOf(unit).isAfter(localTime().endOf(unit))) {
      return null;
    }

    return {
      date,
      start,
      data: { data: Array.from({ length: barAmount }, () => ({ oee: Math.random() })) },
    };
  };

  const [renderChart, setRenderChart] = useState(
    [
      getDate({ initial: selectedDate, action: 'add', amount: 2 }),
      getDate({ initial: selectedDate, action: 'add', amount: 1 }),
      getDate({ initial: selectedDate, start: true }),
      getDate({ initial: selectedDate, action: 'subtract', amount: 1 }),
    ].filter((i) => i),
  );

  const [selectedIndex, setSelectedIndex] = useState(renderChart.findIndex((i) => i.start));

  const selectedBarItem = selectedBarIndex !== null ? renderChart[selectedIndex]?.data?.data?.[selectedBarIndex] : null;

  const saveChartData = (date, data) => {
    setRenderChart((currentValues) => currentValues.map((i) => (i.date === date ? { ...i, data } : i)));
  };

  const loadPastData = () => {
    const date = getDate({ initial: renderChart[renderChart.length - 1].date, action: 'subtract', amount: 1 });
    if (date) {
      setRenderChart([...renderChart, date]);
    }
  };

  const loadFeatureData = () => {
    const date = getDate({ initial: renderChart[0].date, action: 'add', amount: 1 });
    if (date) {
      setRenderChart([date, ...renderChart]);
    }
  };

  const resetData = () => {
    const { legend = [] } = getBarLegend(selectedTime, selectedDate);
    const barAmount = legend.length;

    const newData = [
      getDate({ initial: selectedDate, action: 'add', amount: 2, length: barAmount }),
      getDate({ initial: selectedDate, action: 'add', amount: 1, length: barAmount }),
      getDate({ initial: selectedDate, start: true, length: barAmount }),
      getDate({ initial: selectedDate, action: 'subtract', amount: 1, length: barAmount }),
    ].filter((i) => i);

    setRenderChart(newData);

    const newIndex = newData.findIndex((i) => i.start);
    setSelectedIndex(newIndex);

    if (!isWeb && flatListRef.current) {
      setTimeout(() => {
        flatListRef.current.scrollToIndex({
          index: newIndex,
          animated: false,
        });
      }, 0);
    }
  };

  useEffect(() => {
    if (isWeb) {
      resetData();
    }
  }, [selectedDate, id, selectedTime]);

  useEffect(() => {
    if (!isWeb) {
      resetData();
    }
  }, [id, selectedTime, resetMobileChartCount]);

  const userStopScroll = (number) => {
    const i = Math.floor(number / chartWidth);
    const date = renderChart[i]?.date;
    const { unit: scrollUnit } = getPeriodStep(selectedTime);
    const isEnd = localTime(date).endOf(scrollUnit).isSameOrAfter(localTime().endOf(scrollUnit));

    setSelectedIndex(isEnd ? i : i || 1);
    if (date) {
      setSelectedChartDate(date);
    }
    if (i == 0) {
      loadFeatureData();
    }
    if (i >= renderChart.length - 2) {
      loadPastData();
    }
  };

  const chartHeight = scale(119, false);
  const rightLegendFontOffset = scale(10, false);
  const barBlockWidth = chartWidth / barAmount;
  const barWidth = barBlockWidth * 0.66;
  const isDay = selectedTime === TimePeriod.DAY;

  return {
    selectedTime,
    selectedBarIndex,
    setSelectedBarIndex,
    selectedBarItem,
    renderChart,
    selectedIndex,
    toGo,
    saveChartData,
    userStopScroll,
    flatListRef,
    chartHeight,
    rightLegendFontOffset,
    barBlockWidth,
    barWidth,
    barAmount,
    isDay,
  };
};
